import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import useAuth from "../helper/useAuth";
import UpdateProfileForm from "../components/UpdateProfileForm";
import { useTheme } from "../components/ThemeProvider";

function normalizeUser(data) {
  if (!data || typeof data !== "object") return null;
  return data.user || data.data || data;
}

// Compose an absolute URL for a backend-relative storage path
// (e.g. "/storage/partnerApplications/file.pdf") using the SecondApi origin.
function getStorageFileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = process.env.REACT_APP_SECOND_API_KEY || "";
  try {
    const origin = new URL(base).origin;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${origin}${cleanPath}`;
  } catch (e) {
    return path;
  }
}

function extractFileName(path) {
  if (!path) return "";
  const segments = String(path).split("/").filter(Boolean);
  return segments[segments.length - 1] || "";
}

const TABS = {
  PERSONAL: "personal",
  EMAIL: "email",
  SECURITY: "security",
};

const ProfilePage = ({ openModal, setChangeEmailContext } = {}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.PERSONAL);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/${i18n.language}`, { replace: true });
    }
  }, [isAuthenticated, i18n.language, navigate]);

  const fetchUser = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setLoadError(null);
    const apiLocale = getSecondApiLocale(i18n.language);
    try {
      const { data } = await SecondApi.get(`/api/${apiLocale}/auth/getUserInfo`);
      setUser(normalizeUser(data));
    } catch (error) {
      if (error.code === "ERR_NETWORK" || !error.response) {
        setLoadError(t("profile.errors.network"));
      } else {
        const msg = error.response?.data?.message;
        const status = error.response?.status;
        const statusLabel = status ? `[${status}] ` : "";
        setLoadError(
          `${statusLabel}${
            typeof msg === "string" && msg.trim()
              ? msg
              : t("profile.errors.generic")
          }`,
        );
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, i18n.language, t]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    const handler = () => fetchUser();
    window.addEventListener("nomio:user-updated", handler);
    return () => window.removeEventListener("nomio:user-updated", handler);
  }, [fetchUser]);

  if (!isAuthenticated) return null;

  const handleLogout = async () => {
    setLogoutError(null);
    setLoggingOut(true);
    try {
      await logout(i18n.language || "en");
      navigate(`/${i18n.language}`, { replace: true });
    } catch (e) {
      setLogoutError(t("authModal.logout.error"));
    } finally {
      setLoggingOut(false);
    }
  };

  const handleOpenChangeEmail = () => {
    if (typeof openModal !== "function") return;
    if (typeof setChangeEmailContext === "function") {
      setChangeEmailContext({ currentEmail: user?.email || "" });
    }
    openModal("changeEmail");
  };

  const handleOpenResetPassword = () => {
    if (typeof openModal === "function") {
      openModal("forgotPassword");
    }
  };

  const displayName =
    user?.name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    t("profile.title");
  const displayEmail = user?.email || "—";
  const displayPhone = user?.phone_number || user?.phone || "—";
  const partner = user?.partner || null;
  const partnerStatus = partner?.partner_status || null;
  // True only for approved partners (rejected/pending users are not full partners yet).
  // Falls back to legacy flags for older API responses.
  const isPartner =
    partnerStatus === "approved" ||
    (!partner && Boolean(user?.is_partner || user?.nomio_partner));
  const designation = isPartner
    ? t("profile.partnerBadge")
    : t("profile.subtitle");

  const initials =
    (displayName || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || "")
      .join("") || "?";

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(
        i18n.language === "hy" ? "hy-AM" : "en-US",
        { year: "numeric", month: "short", day: "2-digit" },
      )
    : null;

  return (
    <>
      <div
        className="cover-photo position-relative z-index-1 overflow-hidden"
        style={{
          minHeight: 220,
          background: "var(--main-gradient)",
        }}
      />

      <section
        className="profile-section"
        style={{ paddingBottom: "clamp(80px, 12vw, 180px)" }}
      >
        <div className="container container-two">
          <div className="profile-content-wrapper z-index-1 position-relative mt--100">
            <div className="profile">
              <div className="row gy-4">
                {/* Left: profile info card */}
                <div className="col-xxl-3 col-xl-4">
                  <div
                    className="profile-info bg-white rounded-4 shadow-sm p-4"
                    style={{ position: "sticky", top: 96 }}
                  >
                    <div className="profile-info__inner mb-32 text-center">
                      <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-main text-white fw-600 mb-3"
                        style={{
                          width: 110,
                          height: 110,
                          fontSize: 40,
                          border: "4px solid #fff",
                          boxShadow: "0 4px 18px rgba(0,0,0,0.12)",
                        }}
                        aria-hidden="true"
                      >
                        {loading ? (
                          <i className="las la-user" />
                        ) : (
                          initials
                        )}
                      </div>
                      <h5 className="profile-info__name mb-1">
                        {loading ? (
                          <span
                            className="d-inline-block bg-light rounded"
                            style={{ width: 160, height: 22 }}
                            aria-hidden="true"
                          />
                        ) : (
                          displayName
                        )}
                      </h5>
                      <span className="profile-info__designation font-14 text-body">
                        {designation}
                      </span>
                    </div>

                    {loadError && (
                      <div
                        className="alert alert-danger font-13 py-2"
                        role="alert"
                      >
                        {loadError}
                        <button
                          type="button"
                          onClick={fetchUser}
                          className="link text-decoration-underline fw-500 bg-transparent border-0 p-0 ms-1"
                        >
                          {t("profile.retry")}
                        </button>
                      </div>
                    )}

                    <ul className="profile-info-list list-unstyled mb-0">
                      <ProfileInfoRow
                        iconSrc="/assets/images/icons/profile-info-icon1.svg"
                        label={t("profile.fields.name")}
                        value={loading ? "…" : displayName}
                      />
                      <ProfileInfoRow
                        iconSrc="/assets/images/icons/profile-info-icon2.svg"
                        label={t("profile.fields.email")}
                        value={loading ? "…" : displayEmail}
                      />
                      <ProfileInfoRow
                        iconSrc="/assets/images/icons/profile-info-icon3.svg"
                        label={t("profile.fields.phone")}
                        value={loading ? "…" : displayPhone}
                      />
                      {/* <ProfileInfoRow
                        iconSrc="/assets/images/icons/profile-info-icon4.svg"
                        label={t("profile.partnerLabel")}
                        value={
                          loading
                            ? "…"
                            : isPartner
                            ? t("profile.partnerYes")
                            : t("profile.partnerNo")
                        }
                      /> */}
                      {memberSince && (
                        <ProfileInfoRow
                          iconSrc="/assets/images/icons/profile-info-icon6.svg"
                          label={t("profile.memberSince")}
                          value={memberSince}
                        />
                      )}
                    </ul>

                    <div className="d-grid gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/${i18n.language}`)}
                        className="btn btn-outline-main pill fw-500"
                      >
                        <i className="las la-home me-1" />
                        {t("profile.goHome")}
                      </button>
                      <button
                        type="button"
                        disabled={loggingOut}
                        onClick={handleLogout}
                        className="btn btn-main pill fw-500"
                      >
                        <i className="las la-sign-out-alt me-1" />
                        {loggingOut
                          ? t("authModal.logout.loading")
                          : t("authModal.logout.btn")}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: partner status banner + tabs */}
                <div className="col-xxl-9 col-xl-8 d-flex flex-column gap-3">
                  {/* {!loading && typeof openModal === "function" && (
                    <PartnerStatusBanner
                      partner={partner}
                      isDarkMode={isDarkMode}
                      onApply={() => openModal("partner")}
                    />
                  )} */}

                  <div className="dashboard-card bg-white rounded-4 shadow-sm">
                    <div className="dashboard-card__header pb-0 px-4 pt-4 border-bottom-0">
                      <ul
                        className="nav tab-bordered nav-pills gap-3 flex-wrap"
                        role="tablist"
                      >
                        <TabButton
                          active={activeTab === TABS.PERSONAL}
                          onClick={() => setActiveTab(TABS.PERSONAL)}
                          label={t("profile.tabs.personal")}
                        />
                        <TabButton
                          active={activeTab === TABS.EMAIL}
                          onClick={() => setActiveTab(TABS.EMAIL)}
                          label={t("profile.tabs.email")}
                        />
                        <TabButton
                          active={activeTab === TABS.SECURITY}
                          onClick={() => setActiveTab(TABS.SECURITY)}
                          label={t("profile.tabs.security")}
                        />
                      </ul>
                    </div>

                    <div className="profile-info-content p-4">
                      {logoutError && (
                        <div
                          className="alert alert-danger font-14"
                          role="alert"
                        >
                          {logoutError}
                        </div>
                      )}

                      {activeTab === TABS.PERSONAL && (
                        <PersonalInfoTab
                          user={user}
                          loading={loading}
                          loadError={loadError}
                          onUpdated={fetchUser}
                        />
                      )}

                      {activeTab === TABS.EMAIL && (
                        <EmailTab
                          email={user?.email}
                          loading={loading}
                          onChangeEmail={handleOpenChangeEmail}
                        />
                      )}

                      {activeTab === TABS.SECURITY && (
                        <SecurityTab
                          onResetPassword={handleOpenResetPassword}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// PartnerStatusBanner — shows the partner-application CTA and post-submission
// status (pending / approved / rejected) with the user's signed document.
// This is the production behaviour and stays put even after the demo moves
// to /demo. The "approved" state intentionally does NOT render the dashboard
// here — that's Sprint 2 territory and lives under /demo until backend ships.
// ---------------------------------------------------------------------------
function PartnerStatusBanner({ partner, isDarkMode, onApply }) {
  // CSS in this codebase has an inverted convention where `[data-theme="light"]`
  // styles the dark visual mode. Visual dark mode is when isDarkMode is FALSE.
  const isVisuallyDark = !isDarkMode;
  const { t, i18n } = useTranslation();
  const status = partner?.partner_status || null;
  const filePath = partner?.sign || "";
  const fileUrl = filePath ? getStorageFileUrl(filePath) : "";
  const fileName = filePath
    ? extractFileName(filePath)
    : t("partner.status.documentFallbackName");

  let iconClass = "las la-user";
  let title = t("partner.cta.title");
  let descriptionNode = (
    <span className="font-14 opacity-90">{t("partner.cta.subtitle")}</span>
  );
  let buttonNode = (
    <button
      type="button"
      onClick={onApply}
      className="btn btn-light pill fw-500 px-4"
    >
      {t("partner.cta.btn")}
      <i className="las la-arrow-right ms-1" />
    </button>
  );
  let badgeNode = null;
  let showFile = false;
  let reasonText = "";

  if (!partner) {
    // ---- State 1: no partner record yet — keep the original CTA defaults
  } else if (status === "pending") {
    iconClass = "las la-clock";
    title = t("partner.status.pending.title");
    descriptionNode = (
      <span className="font-14 opacity-90">
        <Trans
          i18nKey="partner.status.pending.description"
          components={{
            contact: (
              <Link
                to={`/${i18n.language}/contact`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white text-decoration-underline fw-600"
              />
            ),
          }}
        />
      </span>
    );
    buttonNode = null;
    badgeNode = (
      <span className="badge bg-warning text-dark fw-600 px-3 py-2 rounded-pill">
        {t("partner.status.badge.pending")}
      </span>
    );
    showFile = true;
  } else if (status === "approved") {
    iconClass = "las la-handshake";
    title = t("partner.status.approved.title");
    descriptionNode = (
      <span className="font-14 opacity-90">
        {t("partner.status.approved.description")}
      </span>
    );
    buttonNode = null;
    badgeNode = (
      <span
        className="badge fw-600 px-3 py-2 rounded-pill"
        style={{ backgroundColor: "#19a06b", color: "#ffffff" }}
      >
        <i className="las la-check-circle me-1" />
        {t("partner.status.badge.approved")}
      </span>
    );
    showFile = true;
  } else if (status === "rejected") {
    iconClass = "las la-exclamation-triangle";
    title = t("partner.status.rejected.title");
    reasonText =
      (partner.message && String(partner.message).trim()) ||
      t("partner.status.rejected.defaultMessage");
    descriptionNode = (
      <div className="font-14 opacity-90 d-flex flex-column gap-2">
        <div className="d-flex flex-column" style={{ gap: 4 }}>
          <span
            className="fw-700 text-uppercase"
            style={{
              fontSize: 11,
              letterSpacing: "0.06em",
              opacity: 0.8,
            }}
          >
            {t("partner.status.rejected.reasonLabel")}
          </span>
          <span className="fst-italic">«{reasonText}»</span>
        </div>
        <span>
          <Trans
            i18nKey="partner.status.rejected.contactHint"
            components={{
              contact: (
                <Link
                  to={`/${i18n.language}/contact`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white text-decoration-underline fw-600"
                />
              ),
            }}
          />
        </span>
      </div>
    );
    buttonNode = (
      <button
        type="button"
        onClick={onApply}
        className="btn btn-light pill fw-500 px-4"
      >
        {t("partner.status.rejected.reapplyBtn")}
        <i className="las la-arrow-right ms-1" />
      </button>
    );
    badgeNode = (
      <span
        className="badge fw-600 px-3 py-2 rounded-pill"
        style={{ backgroundColor: "#e7423a", color: "#ffffff" }}
      >
        <i className="las la-times-circle me-1" />
        {t("partner.status.badge.rejected")}
      </span>
    );
    showFile = true;
  }

  return (
    <div
      className="rounded-4 shadow-sm mb-4 p-4 text-white"
      style={{ background: "var(--main-gradient)" }}
    >
      {badgeNode && (
        <div className="d-flex justify-content-end mb-3">{badgeNode}</div>
      )}

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="d-flex align-items-center gap-3">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{
              width: 52,
              height: 52,
              backgroundColor: isVisuallyDark
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.2)",
            }}
            aria-hidden="true"
          >
            <i
              className={iconClass}
              style={{
                fontSize: "1.8rem",
                lineHeight: 1,
                color: "#ffffff",
              }}
            />
          </span>
          <div>
            <div className="font-18 fw-600 mb-1">{title}</div>
            <div>{descriptionNode}</div>
          </div>
        </div>

        {buttonNode}
      </div>

      {showFile && filePath && (
        <div
          className="mt-4 pt-3 d-flex flex-wrap align-items-center justify-content-between gap-3"
          style={{ borderTop: "1px solid rgba(255, 255, 255, 0.25)" }}
        >
          <div className="d-flex align-items-center gap-2 flex-grow-1 min-width-0">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{
                width: 38,
                height: 38,
                backgroundColor: "rgba(255, 255, 255, 0.18)",
              }}
              aria-hidden="true"
            >
              <i
                className="las la-file-pdf"
                style={{ fontSize: "1.4rem", color: "#ffffff" }}
              />
            </span>
            <div className="d-flex flex-column min-width-0">
              <span className="font-12 opacity-75 text-uppercase fw-600">
                {t("partner.status.documentLabel")}
              </span>
              <span
                className="font-14 fw-500 text-truncate"
                style={{ maxWidth: 320 }}
                title={fileName}
              >
                {fileName}
              </span>
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-light pill fw-500 px-3"
              style={{ borderColor: "rgba(255, 255, 255, 0.6)" }}
            >
              <i className="las la-external-link-alt me-1" />
              {t("partner.status.documentOpen")}
            </a>
            <a
              href={fileUrl}
              download={fileName}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-light pill fw-500 px-3"
            >
              <i className="las la-download me-1" />
              {t("partner.status.documentDownload")}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, label }) {
  return (
    <li className="nav-item" role="presentation">
      <button
        type="button"
        onClick={onClick}
        className={`nav-link font-18 font-heading fw-500 ${
          active ? "active" : ""
        }`}
      >
        {label}
      </button>
    </li>
  );
}

function ProfileInfoRow({ iconSrc, label, value }) {
  return (
    <li className="profile-info-list__item d-flex flex-column gap-1 py-2 border-bottom">
      <span className="profile-info-list__content d-inline-flex align-items-center gap-2 flex-nowrap">
        <img src={iconSrc} alt="" className="icon" style={{ width: 18, height: 18 }} />
        <span className="text text-heading fw-500 font-14">{label}</span>
      </span>
      <span
        className="profile-info-list__info font-14 text-break"
        style={{ paddingLeft: 26 }}
      >
        {value}
      </span>
    </li>
  );
}

function PersonalInfoTab({ user, loading, loadError, onUpdated }) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="py-5 text-center text-body">
        <span
          className="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        />
        {t("profile.loading")}
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="alert alert-danger font-14" role="alert">
        {loadError}
      </div>
    );
  }
  return (
    <UpdateProfileForm
      user={user}
      onSuccess={() => {
        if (typeof onUpdated === "function") onUpdated();
      }}
    />
  );
}

function EmailTab({ email, loading, onChangeEmail }) {
  const { t } = useTranslation();
  return (
    <div className="row gy-3">
      <div className="col-12">
        <h5 className="mb-1 font-20 font-heading fw-600">
          {t("profile.tabs.email")}
        </h5>
        <p className="text-body font-14 mb-0">{t("profile.emailTab.intro")}</p>
      </div>
      <div className="col-12">
        <div className="position-relative">
          <input
            type="email"
            value={loading ? "" : email || ""}
            disabled
            placeholder={loading ? "…" : ""}
            className="common-input common-input--withIcon border"
          />
          <span className="input-icon">
            <img src="/assets/images/icons/envelope-icon.svg" alt="" />
          </span>
        </div>
      </div>
      <div className="col-12">
        <button
          type="button"
          onClick={onChangeEmail}
          disabled={loading}
          className="btn btn-main btn-lg pill"
        >
          <i className="las la-envelope-open-text me-1" />
          {t("profile.changeEmail.btn")}
        </button>
      </div>
    </div>
  );
}

function SecurityTab({ onResetPassword }) {
  const { t } = useTranslation();
  return (
    <div className="row gy-3">
      <div className="col-12">
        <h5 className="mb-1 font-20 font-heading fw-600">
          {t("profile.security.title")}
        </h5>
        <p className="text-body font-14 mb-0">
          {t("profile.security.intro")}
        </p>
      </div>

      <div className="col-12">
        <div className="rounded-3 border p-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <span
              className="d-inline-flex align-items-center justify-content-center bg-main bg-opacity-10 text-main rounded-circle flex-shrink-0"
              style={{ width: 44, height: 44 }}
              aria-hidden="true"
            >
              <i className="las la-key" style={{ fontSize: "1.3rem" }} />
            </span>
            <div>
              <div className="font-16 fw-600">
                {t("profile.security.password")}
              </div>
              <div className="font-13 text-body">
                {t("profile.security.passwordHint")}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onResetPassword}
            className="btn btn-outline-main pill fw-500"
          >
            {t("profile.security.resetPasswordBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
